import { useNavigate, useParams } from "react-router-dom";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById";
import useUsers from "src/api/hooks/useUsers";
import { objectTypes } from "src/consts/objectTypes";
import { lang } from "src/language/Strings";
import { useFiles } from "src/api/hooks/useFiles";
import { useEffect, useMemo } from "react";
import useChangeTitle from "src/api/hooks/useChangeTitle";

import { routes } from "src/routes";
import { ImageGallery } from "./ImageGallery";
import { Button } from "src/components/Buttons/Button";
import { ItemDescription } from "./ItemDescription";
import { useCreateReport } from "src/api/hooks/useReport";
import useMyUser from "src/api/hooks/useMyUser";
import { PopupImageGallery } from "src/routes/companyId/Map/Overlays/PopupImageGallery";
import Toggle from "./Toggle";

const getLocalizedDeviationType = (name: string) => {
  switch (name) {
    case "Urgent":
      return lang.urgent;
    case "Improvement":
      return lang.improvement;
    case "Vandalism":
      return lang.vandalism;
    case "Other":
      return lang.other;
    default:
      return lang.other;
  }
};
const getLocalizedStatusType = (name: string) => {
  switch (name) {
    case "Created":
      return lang.created;
    case "Contact customer":
      return lang.contactCustomer;
    case "Sent to customer":
      return lang.sentToCustomer;
    case "Sold":
      return lang.sold;
    case "Finished":
      return lang.finished;
    case "On Hold":
      return lang.onHold;
    default:
      return lang.other;
  }
};

export function ShowById({ id }: { id: string }) {
  const { companyId, inspectionId } = useParams();
  const { data: currentItem, isSuccess } = useProjectVariationById(id);
  const { data: users } = useUsers();
  const reportMutation = useCreateReport();
  const message = lang.reportCreated;
  const navigate = useNavigate();
  const { data: files, invalidate } = useFiles(currentItem?.id!);
  const { data: myUser } = useMyUser();
  useEffect(() => {
    if (isSuccess) {
      invalidate();
    }
  }, [isSuccess]);

  const ObjectType = useMemo(() => {
    if (
      currentItem?.objectType === objectTypes.Inspection ||
      String(currentItem?.objectType) === "Egenkontroller"
    ) {
      return objectTypes.Inspection;
    }
    if (currentItem?.objectType === objectTypes.Deviation) {
      return objectTypes.Deviation;
    }
    if (currentItem?.objectType === objectTypes.InspectionItem) {
      return objectTypes.InspectionItem;
    }
    return "";
  }, [currentItem?.objectType]);

  const objectTypeTitle = useMemo(() => {
    switch (ObjectType) {
      case objectTypes.Inspection:
        return currentItem?.title;
      case objectTypes.Deviation:
        return lang.deviationFor(currentItem?.title || "");
      default:
        return "";
    }
  }, [currentItem?.id, ObjectType]);

  useChangeTitle(objectTypeTitle || "");
  return (
    <div className="flex-1 h-full w-full max-h-full">
      <div className="border-2 border-Grey2 rounded">
        {currentItem?.id && (
          <ItemDescription label={"ID"}>{currentItem.id}</ItemDescription>
        )}
        {currentItem?.title ? (
          <ItemDescription label={lang.address}>
            {currentItem.title}
          </ItemDescription>
        ) : (
          ""
        )}
        {currentItem?.customer ? (
          <ItemDescription label={lang.customer}>
            {currentItem.customer}
          </ItemDescription>
        ) : (
          ""
        )}
        {currentItem?.deviationType &&
          [objectTypes.Deviation, objectTypes.Inspection].includes(
            ObjectType as "Deviation" | "Inspection"
          ) && (
            <ItemDescription label={lang.deviationType}>
              {getLocalizedDeviationType(currentItem.deviationType)}
            </ItemDescription>
          )}
        {currentItem?.createdAt ? (
          <ItemDescription label={lang.created}>
            {new Date(currentItem.createdAt).toLocaleDateString()}
          </ItemDescription>
        ) : (
          ""
        )}
        
        {ObjectType === objectTypes.Deviation ? (
          <ItemDescription label={lang.statusText}>
            {getLocalizedStatusType(String(currentItem?.status))}
          </ItemDescription>
        ) : (
          currentItem?.status && <ItemDescription label={lang.statusText}>
            <Toggle name="status" falseLabel={lang.notCompleted} falseValue="1" trueLabel={lang.completed} onChange={(e) => {
              currentItem.status = e.target.checked ? "5" : "1";

            }} trueValue="5" defaultValue={currentItem?.status === "5" ? "5" : "1"} />
            {currentItem?.status === "5" ? lang.completed : lang.notCompleted}
          </ItemDescription>
        )}
        {currentItem?.description ? (
          <ItemDescription label={lang.description}>
            {currentItem.description}
          </ItemDescription>
        ) : (
          ""
        )}
        {currentItem?.information ? (
          <ItemDescription label={lang.information}>
            {currentItem.information.includes("https://") ? (
              <a
                href={currentItem.information}
                target="_blank"
                className="text-SkaneGreen"
                rel="noopener noreferrer"
              >
                {lang.link}
              </a>
            ) : (
              currentItem.information
            )}
          </ItemDescription>
        ) : (
          ""
        )}
        {currentItem?.group?.name ? (
          <ItemDescription label={lang.group}>
            {currentItem.group.name}
          </ItemDescription>
        ) : null}

        {currentItem?.user?.firstName ? (
          <ItemDescription label={lang.reportedByUser}>
            {currentItem.user.firstName + " " + currentItem.user?.surname || ""}
          </ItemDescription>
        ) : (
          ""
        )}
        {currentItem?.assignedToUserID ? (
          <ItemDescription label={lang.assignedTo}>
            {users?.find((u) => u.id === currentItem.assignedToUserID)
              ?.firstName +
              " " +
              users?.find((u) => u.id === currentItem.assignedToUserID)
                ?.surname}
          </ItemDescription>
        ) : (
          ""
        )}
        {currentItem?.closingRemarks ? (
          <ItemDescription label={lang.closingRemark}>
            {currentItem.closingRemarks}
          </ItemDescription>
        ) : (
          ""
        )}
        {
          <ItemDescription label={lang.editItemByObjectType(ObjectType)}>
            <button
              id="edit-item-button"
              type="button"
              className="p-2 border-Grey4 border-2 rounded-xl bg-Grey1"
              onClick={() => {
                switch (ObjectType) {
                  case objectTypes.Inspection:
                    navigate(
                      routes.Egenkontroller.Pages.Edit.url(
                        companyId,
                        currentItem?.id
                      )
                    );
                    break;
                  case objectTypes.Deviation:
                    if (currentItem?.parentDeviationId && inspectionId) {
                      navigate(
                        routes.Egenkontroller.Pages.Deviations.Edit.url(
                          companyId,
                          currentItem?.parentDeviationId.toString(),
                          currentItem?.id.toString()
                        )
                      );
                    } else {
                      navigate(
                        routes.Deviation.Pages.Edit.url(
                          companyId,
                          currentItem?.id
                        )
                      );
                    }
                    break;
                  default:
                    break;
                }
              }}
            >
              {lang.editItem}
            </button>
          </ItemDescription>
        }

        {currentItem?.id && (
              <ItemDescription label={lang.showOnMap}>
                <button
                  id="open-on-map-button"
                  type="button"
                  className="p-2 border-Grey4 border-2 rounded-xl bg-Grey1"
                  onClick={() => {
                    navigate(
                      routes.Map.url(companyId!, String(currentItem?.id!))
                    );
                  }}
                >
                  {lang.showOnMap}
                </button>
              </ItemDescription>
            )}   
        {myUser?.id && currentItem?.companyId && (
              <ItemDescription label={lang.report}>
                <Button
                  id="report-button"
                  onClick={() => {
                    reportMutation.mutate({
                      title: currentItem.title,
                      companyId: Number(companyId),
                      jsonContent: JSON.stringify({
                        Id: currentItem.id,
                        ReportType: "single",
                        UserId: myUser?.id,
                        CompanyId: Number(currentItem?.companyId),
                        GroupId: Number(currentItem?.groupId),
                        FilterText: currentItem.title || currentItem.information,
                        Title: currentItem.title,
                      }),
                      userId: myUser.id,
                    });
                  }}
                  toast={{
                    message: lang.reportCreated,
                    messageType: "success",
                    timeout: 3000,
                  }}
                  title={lang.createReport}
                />
              </ItemDescription>
            )}
      </div>
      <PopupImageGallery
        id="show-by-id-image-gallery"
        maxHeight="h-60"
        images={currentItem?.files?.map((f) => ({
          image:
            f.files?.find(
              (file) =>
                file.includes("Original.png") ||
                file.includes("original.png") ||
                file.includes("thumbnail.png") ||
                file
            ) ||
            f.files?.[0] ||
            "",
          thumbnail:
            f.files?.find((file) => file.includes("thumbnail.png")) ||
            f.imageResponse?.urlx32 ||
            f.imageResponse?.urlx240 ||
            f.imageResponse?.urlOriginal ||
            f.files?.[0] ||
            "",
          depiction: f.depiction,
          comment: f.comment,
          action: f.action,
        }))}
      />
    </div>
  );
}
